import { format } from "date-fns";
import { useMutation, useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { getBackendUrl } from "api/common";
import { incidentNoteResponseSchema } from "features/CenterContent/RoleContent/ISirk/types";
import { queryFnGET } from "api/tanStackQuery/helpers";
import { dateTimeFormToRequest } from "shared/form/dateTimeSchema";
import {
  AddReserveAlternativeTransportRequest,
  Supplier,
} from "shared/types/alternativeTransport";
import { axiosClient } from "api/http/config";
import { OperationalPointResponse } from "shared/types/operationalPoints";
import { queryClient } from "api/tanStackQuery/queryClient";

export const useGetTrainStations = () =>
  useQuery<OperationalPointResponse>({
    queryKey: ["trainStations"],
    queryFn: ({ signal }) =>
      queryFnGET<OperationalPointResponse>({
        signal,
        url: `${getBackendUrl()}/operational-point/train-stations`,
      }),
    retry: 1,
  });

export const useGetOperators = () =>
  useQuery<Supplier[]>({
    queryKey: ["operators"],
    queryFn: ({ signal }) =>
      queryFnGET<Supplier[]>({
        signal,
        url: `${getBackendUrl()}/alternativeTransports/operators`,
      }),
    staleTime: 0,
  });

export const useAddVehicle = () =>
  useMutation({
    mutationFn: async (data: AddReserveAlternativeTransportRequest) => {
      const payload = {
        ...data,
        shiftStartTime: dateTimeFormToRequest(data.shiftStartTime),
        shiftEndTime: dateTimeFormToRequest(data.shiftEndTime),
      };

      const response = await axiosClient.post(
        `${getBackendUrl()}/alternativeTransports`,
        payload,
      );

      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["reserveAltTransport"],
      });
      void queryClient.invalidateQueries({
        queryKey: ["backupDetails"],
      });
    },
  });

export const useUpdateVehicle = (id: string) =>
  useMutation({
    mutationFn: async (data: AddReserveAlternativeTransportRequest) => {
      const payload = {
        ...data,
        shiftStartTime: dateTimeFormToRequest(data.shiftStartTime),
        shiftEndTime: dateTimeFormToRequest(data.shiftEndTime),
      };

      const response = await axiosClient.post(
        `${getBackendUrl()}/alternativeTransports/reserve/${id}`,
        payload,
      );

      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["reserveAltTransport"],
      });
      void queryClient.invalidateQueries({
        queryKey: ["backupDetails"],
      });
    },
  });

export const useGetIncidentNotes = (date: Date) =>
  useQuery({
    queryKey: ["iSirk", format(date, "yyyy-MM-dd")],
    queryFn: async ({ signal }) => {
      const res = await axiosClient.get<
        z.infer<typeof incidentNoteResponseSchema>
      >(
        `${getBackendUrl()}/alternative-transport/incident-notes?from=${format(date, "yyyy-MM-dd")}`,
        { signal },
      );
      return incidentNoteResponseSchema.parse(res.data);
    },
  });
